import { Sparkles } from 'lucide-react';
import { useCallback } from 'react';

import { karmaOfferForCpt } from '@/entities/karma';
import { SelectTool, useAgentTweakable } from '@/features/copilot/agent-tweakable';
import type { HintAnchorId } from '@/shared/stores';
import { useHintAnchor } from '@/shared/ui/primitives';
import { TaskStatusRow } from '@/shared/ui/primitives/task-status-row';
import { UrgencyDot } from '@/shared/ui/primitives/urgency-dot';
import type { CareStateCommandMeta } from '@/entities/care-state-domain/commands/types';
import type {
  CareStateRequestStatusT,
  CareStateRequestT,
  CareStateRequestUrgencyT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

const STATUS_OPTIONS = ['Open', 'Pending Follow Up', 'Resolved', 'Cancelled'] as const;

function isValidStatus(v: string): v is CareStateRequestStatusT {
  return (STATUS_OPTIONS as readonly string[]).includes(v);
}

export type CareStateRequestRowProps = {
  request: CareStateRequestT;
  onSetStatus(
    requestId: string,
    status: CareStateRequestStatusT,
    meta?: CareStateCommandMeta
  ): void;
  onSetUrgency(
    requestId: string,
    urgency: CareStateRequestUrgencyT,
    meta?: CareStateCommandMeta
  ): void;
  readonly?: boolean;
  /** Set on the priority row only; engine spotlights the first registered. */
  hintAnchor?: HintAnchorId;
};

function KarmaOfferBadge({ cptCode }: { cptCode: string | null | undefined }) {
  const karma = karmaOfferForCpt(cptCode);
  if (karma <= 0) return null;

  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-1.5 py-px text-[10px] font-semibold tabular-nums text-yellow-600"
      aria-label={`${karma} karma points`}
    >
      <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
      {karma}
    </span>
  );
}

export function CareStateRequestRow({
  request,
  onSetStatus,
  onSetUrgency,
  readonly = false,
  hintAnchor,
}: CareStateRequestRowProps) {
  const safeTitle = request.title.trim().replace(/\s+/g, ' ').replaceAll('.', '_');
  const onAgentSetStatus = useCallback(
    (next: string) => {
      if (!isValidStatus(next)) {
        throw new Error(`Invalid status: ${next}. Allowed: ${STATUS_OPTIONS.join(', ')}`);
      }
      onSetStatus(request.id, next, { actor: 'copilot' });
      return { title: request.title, newStatus: next };
    },
    [onSetStatus, request.id, request.title]
  );
  useAgentTweakable(SelectTool, `setStatus:${safeTitle}`, request.status, onAgentSetStatus);

  const hintRef = useHintAnchor(hintAnchor);

  const row = (
    <TaskStatusRow<CareStateRequestStatusT, CareStateRequestUrgencyT>
      label={request.title}
      status={request.status}
      statusOptions={
        [
          'Open',
          'Pending Follow Up',
          'Resolved',
          'Cancelled',
        ] as ReadonlyArray<CareStateRequestStatusT>
      }
      onStatusChange={(next) => onSetStatus(request.id, next)}
      secondary={{
        label: 'Urgency',
        value: request.urgency,
        options: ['Routine', 'Urgent'] as ReadonlyArray<CareStateRequestUrgencyT>,
        onChange: (next) => onSetUrgency(request.id, next),
        icon: (value) => <UrgencyDot urgency={value} />,
      }}
      leftAccessory={<UrgencyDot urgency={request.urgency} />}
      rightAccessory={
        request.status === 'Open' ? <KarmaOfferBadge cptCode={request.cpt_code} /> : null
      }
      readonly={readonly}
    />
  );

  if (hintAnchor === undefined) {
    return row;
  }
  return (
    <div ref={hintRef} className="flex min-w-0 flex-1 items-center">
      {row}
    </div>
  );
}
